import React, { useEffect, useRef } from 'react';

const Toast = ({ message, type = 'success', onClose, duration = 5000 }) => {
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    // Failures stay available until the user dismisses or resolves them.
    if (type === 'error' || duration <= 0) return undefined;

    const timer = setTimeout(() => {
      onCloseRef.current();
    }, duration);

    return () => clearTimeout(timer);
  }, [message, type, duration]);

  const icon = type === 'success' ? (
    <svg aria-hidden="true" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  ) : type === 'error' ? (
    <svg aria-hidden="true" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
  ) : (
    <svg aria-hidden="true" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );

  return (
    <div
      className="fixed right-[max(1rem,env(safe-area-inset-right))] top-[max(1rem,env(safe-area-inset-top))] z-50 animate-slide-in max-w-[calc(100vw-2rem)] sm:max-w-md"
      role={type === 'error' ? 'alert' : 'status'}
      aria-live={type === 'error' ? 'assertive' : 'polite'}
      aria-atomic="true"
    >
      <div 
        className="px-3 py-2 sm:px-6 sm:py-4 rounded-lg shadow-lg flex items-center gap-2 sm:gap-3 min-w-[200px] sm:min-w-[300px] border text-secondary"
        style={{
          backgroundColor: 'var(--color-accent)',
          borderColor: 'var(--color-primary)',
          color: 'var(--color-secondary)',
        }}
      >
        <div className="flex-shrink-0">{icon}</div>
        <p className="flex-1 font-medium text-sm sm:text-base">{message}</p>
        <button
          type="button"
          onClick={onClose}
          className="flex min-h-11 min-w-11 flex-shrink-0 items-center justify-center rounded transition-colors hover:bg-accent-30 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current sm:min-h-9 sm:min-w-9"
          aria-label="Close notification"
        >
          <svg aria-hidden="true" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default Toast;
